import { Card, CardBody, CardFooter, LaravelResponse, LinkButton, Loading, isSuccess } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";
import styled from "styled-components";
import { shopService } from "../../shared/api/shop/shopService";
import { PaymentTransactionResource, PaymentTransactionStatus } from "../../shared/types/paymentTransaction";
import { ErrorCard } from "../../shared/ui/ErrorCard";

interface Props {
	reason: "success" | "cancel";
}

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	height: 100%;
	justify-content: center;
	align-items: center;
`;

const LoadingWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

const StyledCardBody = styled(CardBody)`
	display: grid;
	grid-auto-rows: 1fr;
	padding: ${({ theme }) => theme.spacing(10)};
	gap: ${({ theme }) => theme.spacing(2)};
`;

const requestUntilNotPending = async (
	request: () => Promise<AxiosResponse<LaravelResponse<PaymentTransactionResource>>>,
): Promise<Exclude<PaymentTransactionStatus, PaymentTransactionStatus.PENDING>> => {
	while (true) {
		const response = await request();
		if (!isSuccess(response)) {
			return PaymentTransactionStatus.ERROR;
		}
		if (response.data.data.status === PaymentTransactionStatus.PENDING) {
			await new Promise((resolve) => setTimeout(resolve, 10000));
		} else {
			return response.data.data.status;
		}
	}
};

export const RegistrationPaymentCallbackPage: FC<Props> = ({ reason }) => {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const transactionId = searchParams.get("dgsTransactionId") || "";
	const token = searchParams.get("token") || "";
	const { data: status } = useQuery({
		queryKey: shopService.completeTransactionKey(transactionId),
		queryFn: () => {
			if (reason === "cancel") {
				return requestUntilNotPending(() => shopService.cancelTransaction(transactionId));
			} else {
				return requestUntilNotPending(() => shopService.completeTransaction(transactionId));
			}
		},
	});

	if (!status) {
		return (
			<Wrapper>
				<Card $variant="outlined">
					<StyledCardBody>
						{t("Your payment is being processed...")}
						<LoadingWrapper>
							<Loading />
						</LoadingWrapper>
					</StyledCardBody>
				</Card>
			</Wrapper>
		);
	}

	switch (status) {
		case PaymentTransactionStatus.COMPLETED:
			return <Navigate to="../success" replace />;
		case PaymentTransactionStatus.CANCELLED:
			return (
				<Wrapper>
					<Card $variant="outlined">
						<CardBody>
							{t("Your payment has been cancelled or did expire. You can return to the registration and try again.")}
						</CardBody>
						<CardFooter>
							<LinkButton to={{ pathname: "../../", search: `token=${token}` }}>
								{t("Return to the registration")}
							</LinkButton>
						</CardFooter>
					</Card>
				</Wrapper>
			);
		case PaymentTransactionStatus.ERROR:
			return (
				<Wrapper>
					<ErrorCard>
						{t(
							"We're sorry, but an error occurred during your payment. Unfortunately, we cannot process your payment at this time.",
						)}
					</ErrorCard>
				</Wrapper>
			);
	}
};
